import { useEffect, useRef, useState } from "react"

/**
 * True while files are dragged over the page; a drop anywhere hands them to `onFiles`. A drop that an
 * element already took (it called preventDefault) is left alone.
 */
export function usePageDrop(onFiles: (files: File[]) => void, enabled = true): boolean {
  const [dragging, setDragging] = useState(false)
  const latest = useRef(onFiles)
  latest.current = onFiles
  useEffect(() => {
    if (!enabled) return
    let depth = 0
    const has = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes("Files")
    const enter = (e: DragEvent) => { if (has(e)) { depth++; setDragging(true) } }
    const over = (e: DragEvent) => { if (has(e)) e.preventDefault() }
    const leave = (e: DragEvent) => { if (has(e) && --depth <= 0) { depth = 0; setDragging(false) } }
    const drop = (e: DragEvent) => {
      if (!has(e)) return
      const taken = e.defaultPrevented
      e.preventDefault()
      depth = 0
      setDragging(false)
      if (taken) return
      latest.current(Array.from(e.dataTransfer?.files ?? []))
    }
    document.addEventListener("dragenter", enter)
    document.addEventListener("dragover", over)
    document.addEventListener("dragleave", leave)
    document.addEventListener("drop", drop)
    return () => {
      document.removeEventListener("dragenter", enter)
      document.removeEventListener("dragover", over)
      document.removeEventListener("dragleave", leave)
      document.removeEventListener("drop", drop)
      setDragging(false)
    }
  }, [enabled])
  return dragging
}
